// ============================================================
// CaseHistorySection.tsx
// Per-case activity timeline — "story of the case". Sits in the
// Case Detail page (ViewingPane) as the first non-Automatic
// section so it's the first thing a solicitor sees when opening
// a case. Read-only; non-sequential (never locks).
//
// Data: GET /api/audit-logs?caseId=<id> via listAuditLogsApi.
// Backend filters to events tagged with this caseId — task.*,
// case.*, case_deadline.* — and returns them newest-first,
// paginated.
//
// Pagination model: LOAD-MORE (not Prev/Next). Per-case timelines
// are typically short and the user expects a single scrollable
// list rather than navigating pages. Each "Load more" click
// appends the next page to the existing list (no replacement).
// Page size: 10 — denser than the Admin viewer's 20 because the
// case page is already content-heavy.
//
// Sentence rendering: shared with the Admin viewer via
// src/utils/auditSentence.tsx (renderActionSentence). One source
// of truth for all event-type templates.
//
// Redesign (final sections pass): drawn in the case shell (SELF_FRAMED) —
// section head ("who changed what" — the read-only audit trail, distinct
// from the billable Activity section) and one surface card of entries with
// hairline dividers. Data, pagination (load-more, 10/page), the firm's
// visibility setting (gated upstream: useCanViewCaseHistory in
// ViewingPane) and the shared sentence renderer are UNCHANGED.
//
// Display states (mutually exclusive):
//   - Loading skeleton (entries === null and not yet errored)
//   - Error banner    (error !== null)
//   - Empty state     ("No activity yet for this case.")
//   - Loaded list     + footer (Load more / "All caught up.")
// ============================================================
import { useLivePermissions } from '../../hooks/useLivePermissions'
import React, { useEffect, useState } from 'react'
import { listAuditLogsApi } from '../../api/casevoApi'
import { useCaseChange } from '../../utils/caseChangeEvents'
import type { AuditLogEntry, Pagination } from '../../types/auditLog.types'
import { renderActionSentence } from '../../utils/auditSentence'
import { relativeTime } from '../../utils/relativeTime'
import { useCasevoStore } from '../../store/useCasevoStore'
import ActorAvatar from '../shared/ActorAvatar'
import { ErrorNote } from '../shared/ActionBox'
import { CARD } from '../clientInfo/ClientInfoSections'
import { SectionHead } from '../case/sections/sectionKit'

// Page size for the case timeline. Smaller than the Admin viewer
// (20) because per-case lists are typically short and the load-more
// model favours smaller pages — fewer clicks to find what you want.
const PAGE_SIZE = 10

interface CaseHistorySectionProps {
  // Threaded as a prop (rather than read from useCasevoStore) so
  // this component is reusable in any context where a caseId is
  // known. Mirrors DocumentsSection's prop pattern in ViewingPane.
  caseId: string
}

const CaseHistorySection: React.FC<CaseHistorySectionProps> = ({ caseId }) => {
  // Accumulator pattern — entries grows across "Load more" clicks.
  // null = initial loading (drives the skeleton).
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null)
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState<number>(1)
  // `loadingMore` is true while a Load-more fetch is in flight. We
  // can't reuse `entries === null` for this because the entries list
  // is already populated past page 1. The button shows "Loading..."
  // while true; disabled state prevents double-fire.
  const [loadingMore, setLoadingMore] = useState<boolean>(false)
  // ★ Live permission change: a lost money right clears the rows (their
  // sentences can carry bill £) AT ONCE; any change re-reads from page 1
  // under the new rights (the server redacts).
  const [reloadNonce, setReloadNonce] = useState(0)
  useLivePermissions((narrowed) => {
    if (narrowed) { setEntries(null); setPagination(null) }
    setPage(1)
    setReloadNonce((n) => n + 1)
  })
  // (D12) Someone else changed the case → page 1 re-reads (newest first). Not
  // while older pages are loaded — the list would jump under the reader.
  useCaseChange(caseId, '*', (_k, info) => { if (info.remote && page === 1) setReloadNonce((n) => n + 1) })

  // Ensure the shared firm-users slice is loaded so actor avatars resolve
  // to real avatars (admins). Load-once + graceful for non-admins (403 →
  // empty slice → ActorAvatar's email-initial fallback).
  const loadFirmUsers = useCasevoStore((s) => s.loadFirmUsers)
  useEffect(() => {
    void loadFirmUsers()
  }, [loadFirmUsers])

  // Fetch on mount + on every `page` change. Cancelled-flag pattern
  // mirrors AuditLogPanel and the other in-page fetches — a quick
  // double-click on Load more no longer surfaces a stale earlier
  // response after the user navigates away mid-fetch.
  //
  // Append semantics: page 1 REPLACES the list (initial load or
  // remount); page > 1 APPENDS to the existing list. The previous
  // `entries` is read via the functional setter so the effect's
  // closure doesn't capture a stale snapshot.
  useEffect(() => {
    let cancelled = false
    if (page > 1) setLoadingMore(true)
    listAuditLogsApi(page, PAGE_SIZE, undefined, undefined, caseId)
      .then((response) => {
        if (cancelled) return
        if (page === 1) {
          setEntries(response.data)
        } else {
          setEntries((prev) => prev ? [...prev, ...response.data] : response.data)
        }
        setPagination(response.pagination)
        setError(null)
      })
      .catch((err) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Failed to load history'
        setError(message)
      })
      .finally(() => {
        if (cancelled) return
        setLoadingMore(false)
      })
    return () => { cancelled = true }
    // caseId is a stable prop for the lifetime of this section
    // (Case Detail unmounts/remounts on case change). Adding it
    // to deps is defensive — covers any future scenario where the
    // section is mounted under a different case without unmount.
  }, [page, caseId, reloadNonce])

  // Derived flag — there's another page to fetch. Pagination is
  // null on the very first render (pre-fetch), so we default to
  // false (button hidden) until the first response lands.
  const hasMore = pagination !== null && page < pagination.totalPages

  const handleLoadMore = (): void => {
    if (!hasMore || loadingMore) return
    setPage((p) => p + 1)
  }

  return (
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      <SectionHead title="History" sub="Who changed what on this case. A read-only audit trail, separate from billable Activity." />
      {error ? (
        <ErrorNote>{error}</ErrorNote>
      ) : entries === null ? (
        // Initial-load skeleton — themed placeholders for light + dark.
        <div className={`${CARD} overflow-hidden`} aria-busy="true" aria-label="Loading history">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-start gap-3 px-[18px] py-[14px] border-t first:border-t-0 border-casevo-border-soft animate-pulse">
              <div className="w-7 h-7 rounded-full bg-casevo-border-soft shrink-0" />
              <div className="flex-1"><div className="w-3/4 h-3.5 bg-casevo-border-soft rounded mb-2" /><div className="w-24 h-2.5 bg-casevo-border-soft rounded" /></div>
            </div>
          ))}
        </div>
      ) : entries.length === 0 ? (
        <div className={`${CARD} px-6 py-9 text-center text-[13.5px] text-casevo-text-soft`}>
          No activity yet for this case.
        </div>
      ) : (
        <>
          <ul className={`${CARD} overflow-hidden m-0 p-0 list-none`}>
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-start gap-3 px-[18px] py-[13px] border-t first:border-t-0 border-casevo-border-soft">
                <ActorAvatar actorUserId={entry.actorUserId} actorEmail={entry.actorEmail} />
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-[14px] text-casevo-forest leading-[1.45]">
                    {renderActionSentence(entry)}
                  </p>
                  <p className="m-0 text-[12px] text-casevo-text-soft mt-[3px]" title={new Date(entry.createdAt).toLocaleString()}>
                    {relativeTime(entry.createdAt)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          {/* Footer — Load more while more pages remain; once exhausted, a
              small "All caught up." note so the list reads as complete. */}
          {hasMore ? (
            <div className="flex justify-center mt-[10px]">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="inline-flex items-center h-[30px] px-[11px] rounded-[8px] text-[12.5px] font-medium border border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : (
            entries.length >= PAGE_SIZE && (
              <p className="text-center text-[12px] text-casevo-text-soft mt-[10px] mb-0">All caught up.</p>
            )
          )}
        </>
      )}
    </div>
  )
}

export default CaseHistorySection
